import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { configSettings } from "../config/configSettings";
import VehicleReadingService from "../services/vehicleReadingService";
import Breadcrumb from "../components/Breadcrumb";
import StorageService from "../services/StorageService";
import Loading from "../components/loading";
import ReactPaginate from "react-paginate";
import XLSX from "xlsx-js-style";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";
import utility from "../utils/utility";
import MessageDialog from "../components/MessageDialog";
import ProjectService from "../services/ProjectService";

export default function ViewVehicleReadings() {
  const [readings, setReadings] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [message, setMessage] = useState("");

  const calculateSingleReading = (rd) => {
    const fromReading = rd.fromReading !== null && rd.fromReading !== "" ? Number(rd.fromReading) : null;
    const toReading = rd.toReading !== null && rd.toReading !== "" ? Number(rd.toReading) : null;

    if (fromReading !== null && toReading !== null && Number.isFinite(fromReading) && Number.isFinite(toReading)) {
      return toReading - fromReading;
    } else if (rd.startTime && rd.endTime) {
      const diff = new Date(rd.endTime) - new Date(rd.startTime);
      const diffMins = Math.round(diff / (1000 * 60));
      return diffMins / 60;
    }
    return 0;
  };

  const getCumulativeReading = (r) => {
    if (!r.readings) return "0.00";
    const activeReadings = r.readings.filter(rd => rd.status?.toLowerCase() === "active");
    const targetReadings = activeReadings.length > 0 ? activeReadings : r.readings;
    const sum = targetReadings.reduce((acc, rd) => acc + calculateSingleReading(rd), 0);
    return sum.toFixed(2);
  };
  const [msgType, setMsgType] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  // const [selectedDate, setSelectedDate] = useState("");
  const [filterProjects, setFilterProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");


  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImages, setCurrentImages] = useState({ fromImg: "", toImg: "" });

  const [showReadingsModal, setShowReadingsModal] = useState(false);
  const [currentReadings, setCurrentReadings] = useState([]);

  const navigate = useNavigate();
  // const messageTimer = useRef(null);

  // const fetchVehicleReadings = async () => {
  //   setLoading(true);
  //   try {
  //     const companyId = StorageService.activeCompany().companyId;
  //     const res = await VehicleReadingService.getByCompanyId(companyId);
  //     const data = res.data || [];
  //     setReadings(data);
  //   } catch (error) {
  //     console.error(error);
  //     showMessage("Error fetching vehicle readings", "error");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchVehicleReadings = async () => {
    setLoading(true);
    try {
      const companyId = StorageService.activeCompany().companyId;
      const employeeId = StorageService.loginData().employeeId;
      const roleId = StorageService.loginData().roleId;

      const res = await VehicleReadingService.getByCompanyId(
        companyId,
        currentPage,
        itemsPerPage,
        selectedProjectId,
        searchText,
        fromDate,
        toDate,
        employeeId,
        roleId
      );

      setReadings(res.data || []);
      setPageCount(res.pageNation?.totalPages || 0);

    } catch (error) {
      console.error(error);
      showMessage("Error fetching vehicle readings", "error");
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {

    if (!can("canView")) {
      showMessage("No permission", "error");
      return;
    }
    try {
      const company = StorageService.activeCompany();
      const login = StorageService.loginData();
      const employeeId = login.employeeId;
      const roleId = login.roleId;

      const res = await VehicleReadingService.exportVehicleReading(
        company.companyId,
        selectedProjectId,
        searchText,
        fromDate,
        toDate,
        employeeId,
        roleId
      );

      if (!res?.status || !res.data.length) {
        showMessage("No data found for selected date", "error");
        return;
      }

      const data = res.data;

      const wb = XLSX.utils.book_new();
      const ws = {};
      ws["!merges"] = [];

      let row = 0;

      // ✅ STYLES
      const thin = { style: "thin" };
      const thick = { style: "medium" };

      const center = {
        horizontal: "center",
        vertical: "center",
        wrapText: true
      };

      // =========================
      // TITLE
      // =========================
      XLSX.utils.sheet_add_aoa(ws, [["Vehicle Reading Report"]], {
        origin: { r: row, c: 1 }
      });

      ws["!merges"].push({
        s: { r: row, c: 1 },
        e: { r: row, c: 13 }
      });

      const titleCell = XLSX.utils.encode_cell({ r: row, c: 1 });

      ws[titleCell].s = {
        font: { bold: true, sz: 16 },
        alignment: center
      };

      row += 2;

      // =========================
      // HEADER
      // =========================
      const header = [
        "Company",
        "Project",
        "Project Code",
        "Vehicle Type",
        "Vehicle",
        "Registration No",
        "Reading Type",
        "Reading Date",
        "From Reading",
        "To Reading",
        "Start Time",
        "End Time",
        "Status"
      ];

      const startRow = row; // ✅ START TRACK

      XLSX.utils.sheet_add_aoa(ws, [header], {
        origin: { r: row, c: 1 }
      });

      header.forEach((_, i) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: i + 1 });

        ws[cell].s = {
          font: { bold: true },
          alignment: center
        };
      });

      row++;

      // =========================
      // DATA
      // =========================
      data.forEach((v) => {
        const rowData = [
          v.companyName,
          v.projectName,
          v.projectCode,
          v.vehicleType || "-",
          v.vehicleName,
          v.registrationNo,
          v.readingType,
          utility.formatDateDDMMMYYYY(v.readingDate),
          v.fromReading,
          v.toReading,
          v.startTime ? utility.formatDbDateTime(v.startTime) : "",
          v.endTime ? utility.formatDbDateTime(v.endTime) : "",
          v.status
        ];

        XLSX.utils.sheet_add_aoa(ws, [rowData], {
          origin: { r: row, c: 1 }
        });

        row++;
      });

      const endRow = row - 1; // ✅ END TRACK

      // =========================
      // APPLY BORDERS 🔥
      // =========================
      const colCount = header.length;

      for (let r = startRow; r <= endRow; r++) {
        for (let c = 1; c <= colCount; c++) {
          const cell = XLSX.utils.encode_cell({ r, c });

          if (!ws[cell]) {
            ws[cell] = { t: "s", v: "" };
          }

          ws[cell].s = ws[cell].s || {};
          ws[cell].s.alignment = center;

          ws[cell].s.border = {
            top: r === startRow ? thick : thin,
            bottom: r === endRow ? thick : thin,
            left: c === 1 ? thick : thin,
            right: c === colCount ? thick : thin
          };
        }
      }

      // =========================
      // COLUMN WIDTH
      // =========================
      ws["!cols"] = [
        { wch: 20 },
        { wch: 20 },
        { wch: 15 },
        { wch: 18 },
        { wch: 20 },
        { wch: 18 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 20 },
        { wch: 20 },
        { wch: 12 }
      ];

      // =========================
      // EXPORT
      // =========================
      XLSX.utils.book_append_sheet(wb, ws, "VehicleReading");

      XLSX.writeFile(wb, "VehicleReading.xlsx");

    } catch (error) {
      console.error(error);
      showMessage("Error exporting data", "error");
    }
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    // if (messageTimer.current) clearTimeout(messageTimer.current);
    // messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1);
  };
  const { permission, checkView, can } = usePermission(
    ScreenData.Vehicle_Reading,
    showMessage
  );

  useEffect(() => {
    if (permission) {
      if (checkView()) {
        fetchVehicleReadings();
      }
    }
  }, [currentPage, searchText, selectedProjectId, fromDate, toDate, permission]);

  useEffect(() => {
    const loadFilterProjects = async () => {
      try {
        const companyId = StorageService.activeCompany().companyId;
        const employeeId = StorageService.loginData().employeeId;
        const roleId = Number(StorageService.loginData().roleId);

        const isPrivileged = roleId === 1 || roleId === 2;
        let list = [];
        if (isPrivileged) {
          const res = await ProjectService.getActive(companyId);
          list = Array.isArray(res) ? res : (res?.data || res || []);
        } else {
          const res = await ProjectService.getAssignedProjects(companyId, employeeId);
          list = res?.data?.data || [];
        }
        setFilterProjects(list);
      } catch (err) {
        console.error("Error loading filter projects", err);
        setFilterProjects([]);
      }
    };
    if (permission) {
      loadFilterProjects();
    }
  }, [permission]);

  // const closeMessage = () => {
  //   setMessage("");
  //   if (messageTimer.current) clearTimeout(messageTimer.current);
  // };

  //   // 🔍 FRONTEND SEARCH FILTER
  // const filteredReadings = readings.filter((r) => {
  //   return (
  //     r.projectName?.toLowerCase().includes(searchText.toLowerCase()) ||
  //     r.vehicleName?.toLowerCase().includes(searchText.toLowerCase()) ||
  //     r.registrationNo?.toLowerCase().includes(searchText.toLowerCase()) ||
  //     r.readingType?.toLowerCase().includes(searchText.toLowerCase())
  //   );
  // });

  const handleSelect = (row) => {
    setSelectedRow(row);
  };

  const handleAdd = () => {
    if (!can("canAdd")) {
      showMessage("No permission", "error");
      return;
    }
    navigate("/cpnl/vehiclereading/add");
  };

  // const handleModify = () => {
  //   if (!selectedRow) {
  //     showMessage("Select one record to modify");
  //     return;
  //   }
  //   navigate(`/cpnl/vehiclereading/add/${selectedRow.readings[0].id}`);
  // };

  const handleModify = () => {
    if (!can("canModify")) {
      showMessage("No permission", "error");
      return;
    }
    if (!selectedRow) {
      showMessage("Select one record to modify");
      return;
    }

    // CHECK ACTIVE READINGS
    const activeReadings = (selectedRow.readings || []).filter(
      (r) => r.status?.toLowerCase() === "active"
    );

    if (activeReadings.length === 0) {
      showMessage("No active readings to modify", "error");
      return;
    }

    const emp = StorageService.loginData();
    const isPrivileged =
      emp.roleName === "Super Admin" ||
      emp.roleName === "SuperAdmin" ||
      emp.roleName === "Data Admin" ||
      emp.roleName === "DataAdmin" ||
      Number(emp.roleId) === 1 ||
      Number(emp.roleId) === 2;

    if (!isPrivileged) {
      const today = new Date().toISOString().slice(0, 10);
      const rowDate = selectedRow.readingDate?.slice(0, 10);

      if (rowDate !== today) {
        showMessage("Only today's readings can be modified", "error");
        return;
      }
    }

    navigate(`/cpnl/vehiclereading/add/${selectedRow.readings[0].id}`);
  };



  const handleViewImages = async () => {

    if (!can("canView")) {
      showMessage("No permission", "error");
      return;
    }
    if (!selectedRow) {
      showMessage("Please select one record.");
      return;
    }

    const firstReading = selectedRow.readings[0];
    const baseURL = `${configSettings.mediaURL}/get/vehicleReading`;

    setCurrentImages({
      fromImg: firstReading?.fromReadingImg
        ? `${baseURL}/${firstReading.fromReadingImg}`
        : "",
      toImg: firstReading?.toReadingImg
        ? `${baseURL}/${firstReading.toReadingImg}`
        : "",
    });
    setShowImageModal(true);
  };

  const handleViewReadings = () => {
    if (!can("canView")) {
      showMessage("No permission", "error");
      return;
    }
    if (!selectedRow) {
      showMessage("Please select one record.");
      return;
    }
    setCurrentReadings(selectedRow.readings || []);
    setShowReadingsModal(true);
  };

  // const handleToggleStatus = async (reading) => {
  //   if (!can("canModify")) {
  //     showMessage("No permission", "error");
  //     return;
  //   }
  //   const newStatus = reading.status === "Active" ? "Inactive" : "Active";
  //   if (!window.confirm(`Are you sure you want to change status to ${newStatus}?`))
  //     return;

  //   setLoading(true);
  //   try {
  //     const res = await VehicleReadingService.updateStatus(reading.id);
  //     showMessage(res.message || "Status updated successfully", "success");
  //     fetchVehicleReadings();
  //   } catch (err) {
  //     console.error(err);
  //     showMessage("Error updating status", "error");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleToggleStatus = async (reading) => {
    if (!can("canModify")) {
      showMessage("No permission", "error");
      return;
    }

    const newStatus = reading.status === "Active" ? "Inactive" : "Active";

    if (!window.confirm(`Are you sure you want to change status to ${newStatus}?`))
      return;

    setLoading(true);
    try {
      const res = await VehicleReadingService.updateStatus(reading.id);

      showMessage(res.message || "Status updated successfully", "success");

      // ✅ 1. Update modal instantly
      setCurrentReadings((prev) =>
        prev.map((r) =>
          r.id === reading.id
            ? { ...r, status: newStatus }
            : r
        )
      );

      // ✅ 2. UPDATE MAIN STATE ALSO (THIS IS THE FIX 🔥)
      setReadings((prev) =>
        prev.map((row) => ({
          ...row,
          readings: row.readings?.map((r) =>
            r.id === reading.id
              ? { ...r, status: newStatus }
              : r
          ),
        }))
      );

    } catch (err) {
      console.error(err);
      showMessage("Error updating status", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb pageTitle="Vehicle Reading" secondTitle="Vehicle Management" />

      {/* {message && ( 
        <div
          className={`alert ${
            msgType === "success" ? "alert-success" : "alert-danger"
          } d-flex justify-content-between align-items-center mb-2 p-2`}
        >
          <span>{message}</span>
          <button type="button" className="btn-close" onClick={closeMessage}></button>
        </div>
      )} */}

      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />


      <div className="row">
        <div className="card py-3 bg-white shadow-sm">
          {/* <div className="mb-3 pb-2 border-bottom">
            <button className="btn btn-success me-2" onClick={handleAdd}>
              <i className="ti ti-plus me-1"></i> Add
            </button>

            <button className="btn btn-warning me-2" onClick={handleModify}>
              <i className="ti ti-pencil me-1"></i> Modify
            </button>

            <button className="btn btn-info me-2" onClick={handleViewImages}>
              <i className="ti ti-eye me-1"></i> From/To Images
            </button>

            <button className="btn btn-primary" onClick={handleViewReadings}>
              <i className="ti ti-list me-1"></i> View Readings
            </button>
          </div> */}

          {/* Filters Row */}
          <div className="d-flex align-items-center gap-3 flex-wrap mb-3 pb-2 border-bottom">
            <div className="d-flex flex-column">
              <label className="form-label mb-1 fw-semibold" style={{ fontSize: "14px" }}>Project</label>
              <select
                className="form-select w-auto"
                value={selectedProjectId}
                onChange={(e) => {
                  setSelectedProjectId(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Projects</option>
                {filterProjects.map((p) => (
                  <option key={p.id || p.projectId} value={p.id || p.projectId}>
                    {p.projectName || p.name}-{p.projectCategoryName || p.CategoryName}-{p.projectCode || p.projectCode}
                  </option>
                ))}
              </select>
            </div>

            <div className="d-flex flex-column">
              <label className="form-label mb-1 fw-semibold" style={{ fontSize: "14px" }}>Vehicle No</label>
              <input
                type="text"
                className="form-control w-auto"
                placeholder="Search Vehicle No"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="d-flex flex-column">
              <label className="form-label mb-1 fw-semibold" style={{ fontSize: "14px" }}>From Date</label>
              <input
                type="date"
                className="form-control w-auto"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="d-flex flex-column">
              <label className="form-label mb-1 fw-semibold" style={{ fontSize: "14px" }}>To Date</label>
              <input
                type="date"
                className="form-control w-auto"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="d-flex flex-column align-self-end">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setSelectedProjectId("");
                  setSearchText("");
                  setFromDate("");
                  setToDate("");
                  setCurrentPage(1);
                }}
              >
                Reset
              </button>
            </div>
          </div>

          {/* Actions Row */}
          <div className="mb-3 pb-2 border-bottom d-flex justify-content-end gap-2 flex-wrap">
            {can("canAdd") && (
              <button className="btn btn-success" onClick={handleAdd}>
                <i className="ti ti-plus me-1"></i> Add
              </button>
            )}

            {can("canModify") && (
              <button className="btn btn-warning" onClick={handleModify}>
                <i className="ti ti-pencil me-1"></i> Modify
              </button>
            )}

            {can("canView") && (
              <button className="btn btn-info" onClick={handleViewImages}>
                <i className="ti ti-eye me-1"></i> From/To Images
              </button>
            )}

            {can("canView") && (
              <button className="btn btn-primary" onClick={handleViewReadings}>
                <i className="ti ti-list me-1"></i> View Readings
              </button>
            )}

            {can("canView") && (
              <button className="btn btn-success" onClick={exportExcel}>
                <i className="ti ti-download me-1"></i> Export Excel
              </button>
            )}
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>Select</th>
                  <th>Reading Date</th>
                  <th>Project</th>
                  <th>Vehicle Type</th>
                  <th>Vehicle</th>
                  <th>Reading Type</th>
                  <th>working Status</th>
                  <th>Driver Name</th>
                  <th>Work Description</th>
                  <th>Total Readings</th>
                  <th>Cumulative Reading</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="12" className="text-center py-4">
                      <Loading />
                    </td>
                  </tr>
                ) : readings.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center text-muted">
                      No vehicle readings found
                    </td>
                  </tr>
                ) : (
                  readings.map((r, index) => {
                    // const firstReading = r.readings?.[0];
                    // const isActive = firstReading?.status?.toLowerCase() === "active";

                    const hasActive = r.readings?.some(
                      (rd) => rd.status?.toLowerCase() === "active"
                    );

                    const rowStatus = hasActive ? "Active" : "Inactive";
                    return (
                      <tr key={index}>
                        <td>
                          <input
                            type="radio"
                            name="vehicleReadingSelect"
                            checked={selectedRow === r}
                            onChange={() => handleSelect(r)}
                          />
                        </td>
                        <td>{utility.formatDateDDMMMYYYY(r.readingDate)}</td>
                        <td>{r.projectName || "-"}-{r.projectCode || "-"}</td>
                        <td>{r.vehicleType || "-"}</td>
                        <td>{r.vehicleName || "-"}-{r.registrationNo || "-"}</td>
                        <td>{r.readingType || "-"}</td>
                        <td>{r.workingStatus || r.readings?.[0]?.workingStatus || "-"}</td>
                        <td>{r.driverName || r.readings?.[0]?.driverName || "-"}</td>
                        <td>{r.workDescription || r.readings?.[0]?.workDescription || "-"}</td>
                        <td>{r.readings?.length || 0}</td>
                        <td>{getCumulativeReading(r)}</td>

                        <td>
                          <span
                            className={`badge ${hasActive ? "bg-success" : "bg-secondary"
                              }`}
                          >
                            {rowStatus}
                          </span>
                        </td>

                        {/* <td>
                          <span
                            className={`badge ${
                              isActive ? "bg-success" : "bg-secondary"
                            }`}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleToggleStatus(firstReading)}
                          >
                            {isActive ? "Active" : "Inactive"}
                          </span>
                        </td> */}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={handlePageClick}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            pageCount={pageCount}
            previousLabel="< Previous"
            containerClassName="pagination-container"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            activeClassName="active"
            forcePage={Math.max(0, currentPage - 1)}
          />
        </div>
      </div>

      {/* 🔹 IMAGE MODAL */}
      {showImageModal && (
        <div className="modal-backdrop-custom">
          <div className="modal-custom">
            <h5 className="mb-3">Vehicle Reading Images</h5>

            <div style={{ display: "flex", gap: "20px" }}>
              <div style={{ flex: 1, textAlign: "center" }}>
                <label className="form-label fw-bold">From Reading Image</label>
                {currentImages.fromImg ? (
                  <img
                    src={currentImages.fromImg}
                    alt="From Reading"
                    className="img-fluid rounded border"
                  />
                ) : (
                  <p className="text-muted">No Image Available</p>
                )}
              </div>

              <div style={{ flex: 1, textAlign: "center" }}>
                <label className="form-label fw-bold">To Reading Image</label>
                {currentImages.toImg ? (
                  <img
                    src={currentImages.toImg}
                    alt="To Reading"
                    className="img-fluid rounded border"
                  />
                ) : (
                  <p className="text-muted">No Image Available</p>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end mt-3">
              <button
                className="btn btn-secondary"
                onClick={() => setShowImageModal(false)}
              >
                <i className="ti ti-x me-1"></i> Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔹 READINGS MODAL (UPDATED) */}
      {showReadingsModal && (
        <div className="modal-backdrop-custom">
          <div className="modal-custom" style={{ width: "850px" }}>
            <h5 className="mb-3">Vehicle Readings Details</h5>

            <table className="table table-bordered table-striped">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Reading Type</th>
                  <th>Start Reading</th>
                  <th>End Reading</th>
                  <th>Start Date/Time</th>
                  <th>End Date/Time</th>
                  <th>Total Reading</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentReadings.map((r, idx) => {
                  const isActive = r.status?.toLowerCase() === "active";

                  return (
                    <tr key={r.id}>
                      <td>{idx + 1}</td>
                      <td>{selectedRow?.readingType || "-"}</td>
                      <td>{r.fromReading ?? "-"}</td>
                      <td>{r.toReading ?? "-"}</td>

                      <td>
                        {r.startTime ? utility.formatDbDateTime(r.startTime) : "-"}
                      </td>
                      <td>
                        {r.endTime ? utility.formatDbDateTime(r.endTime) : "-"}
                      </td>

                      <td>
                        {calculateSingleReading(r) > 0
                          ? calculateSingleReading(r).toFixed(1)
                          : "-"}
                      </td>

                      {/* ✅ STATUS ONLY HERE */}
                      <td>
                        <span
                          className={`badge ${isActive ? "bg-success" : "bg-secondary"
                            }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleToggleStatus(r)}
                        >
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>





            <div className="d-flex justify-content-end mt-3">
              <button
                className="btn btn-secondary"
                onClick={() => setShowReadingsModal(false)}
              >
                <i className="ti ti-x me-1"></i> Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔹 Simple modal styles */}
      <style>{`
        .modal-backdrop-custom {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.5);
          z-index: 999;
        }
        .modal-custom {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: #fff;
          padding: 20px;
          border-radius: 8px;
          z-index: 1000;
          max-height: 80vh;
          overflow-y: auto;
        }
      `}</style>
    </>

  );
}
